<template>
  <div>
    <div class="section-header">
      <h1>Settings</h1>
    </div>
    <div class="card-rows">
      <div class="card card--transparent">
        <h2 style="text-align: center">User Information</h2>
        <form @submit.prevent="saveSettings">
          <div class='input'>
            <label>First Name</label>
            <input
              v-model="user.first_name"
              outline
            />
          </div>
          <div class="input">
            <label>Last Name</label>
            <input
              v-model="user.last_name"
              outline
            />
          </div>
          <div class="input">
            <label>Email</label>
            <input
              v-model="user.email"
              outline
              />
          </div>
          <div class="input">
            <label>Phone Number</label>
            <input v-model="user.phone_number"/>
          </div>
          <div class="outline">
            <div>
              <label>Profile Image</label>
              <user-img :image="displayImage || user.image" />
            </div>
            <input type="file"
              @change="imageChange($event.target.name, $event.target.files); fileCount = $event.target.files.length"
              accept="image/*"
              class="input-file"/>
          </div>
          <button class="btn btn--outline" type='submit' :disabled="loading" color="primary">Save</button>
        </form>
      </div>
      <form @submit.prevent="saveSettings" class="card card--transparent" v-if="user.profile.allow_daily_exercises || user.profile.allow_self_reflect">
        <h2>System Settings</h2>
        <div class="input">
          <label>Timezone</label>
          <select v-model="user.timezone" class="input-outline">
            <option 
              v-for="timezone in timezones"
              :key="timezone"
              :value="timezone">{{timezone}}</option>
          </select>
        </div>

        <div class="input" v-if="user.profile.allow_daily_exercises">
          <label>
            What days of the week would you like to receive your Coach's recommended exercises?
          </label>
          <week-btns :week="user.work_week" color="#70BFF6" />
        </div>
        <div class="input" v-if="user.profile.allow_daily_exercises">
          <label>
            What time would you like to receive these exercises? (Should be at the start of your day)
          </label>
          <timepicker
            class="input-outline"
            v-model="user.generate_exercises_at" />
        </div>
        <div class="input" v-if="user.profile.allow_self_reflect">
          <label>
            When would you like reminders to do daily reflections?
          </label>
          <week-btns :week="user.generate_pulse_on" color="#70BFF6"/>
        </div>
        <div class="input" v-if="user.profile.allow_self_reflect">
          <label>
            When should we send you reminders to take your daily reflections? (Normally should be the end of your day)
          </label>
          <timepicker class="input-outline" v-model="user.generate_pulse_at" />
        </div>
        <button class="btn btn--outline small-top" type="submit" :disabled='loading'>Save</button>
      </form>
      <form @submit.prevent="saveSettings" class="card card--transparent">
        <h2>Preferences</h2>
        <h-input :reverse="true" type="checkbox" v-model="user.allow_notice_emails" label="Allow email notifications" />
        <h-input :reverse="true" type="checkbox" v-model="user.allow_notice_sms" label="Allow SMS (text messages) notifications" />
        <h-input :reverse="true" type="checkbox" v-model="user.allow_volume" label="Allow Sound" />
        <h-input :reverse="true" type="checkbox" v-model="user.raffle_opt_in" label="Participate in 'Make the Change Campaigns'" />
        <div class="input">
          <label>How would you like to be rewarded?</label>
          <select v-model="user.reward" class="input-outline">
            <option value="img">Funny memes and gifs</option>
            <option value="quote">Inspiring quotes</option>
            <option value="any">Any</option>
            <option value="none">None</option>
          </select>
        </div>
        <button class="btn btn--outline small-top" type="submit" :disabled="loading">Save</button>
      </form>

      <div class="card card--transparent" v-if="user.profile.allow_daily_exercises">
        <h2>Current Habit Program</h2>
        <choose-habit-program v-if="user.id" :user="user" :data="userProgram" class="input-outline full-width" />
        <button class="btn btn--outline small-top" @click="updateHabitProgram" :disabled="loading">Save</button>
      </div>
      <form class="card card--transparent" v-if="subscription.id">
        <h2>Subscription</h2>
        <div>Started On: {{ convertDate(subscription.created_at) }}</div>
        <div v-if="subscription.renewing">Next Payment Due On: {{  convertDate(subscription.scheduled_invoice_at) }}</div>
        <div v-if="!subscription.renewing">Active Until: {{ convertDate(subscription.scheduled_invoice_at) }}</div>
        <change-card />
        <button type="button" class="btn btn--outline" @click="cancelSubscription" v-if="subscription.renewing">Cancel Subscription</button>
      </form>
      <div class="card card--transparent">
        <h2>Reset Password</h2>
        <div class="error-msg" v-if="passwordErrors" >
          {{ Object.keys(passwordErrors).map(i => passwordErrors[i][0])[0] }}
        </div>
        <form @submit.prevent="updatePassword">
          <div class="input">
            <label>Current Password</label>
            <input
              v-model="oldPassword"
              required
              type="password"
            />
          </div>
          <div class="input">
            <label>New Password</label>
            <input
              v-model="password"
              required
              type="password"
            />
          </div>
          <div class="input">
            <label>Confirm Password</label>
            <input
              v-model="confirmation"
              required
              type="password"
            />
          </div>
          <button class="btn btn--outline small-top" type="submit" :disabled="loading">Save</button>
        </form>
      </div>

      <client-subscription :subscription="cLastSubscription" v-if="cLastSubscription" />
    </div>
    <div class="section-headers">App Version {{ appVersion }}</div>
  </div>
</template>

<script>
import User from "@/services/api/authUser";
import dataStore from "@/services/api/store";
import moment from "moment";
import timePrefences from "../components/timePrefences";
import chooseHabitProgram from "../components/chooseHabitProgram";
import changeEmail from "./changeEmail";
import axios from "axios";
import userImg from "@/components/userImg";
import changeCard from "./updateCreditCard";
import snackbar from "@/services/hubs/snackbar";
import timezones from "../timezones";
import timepicker from "@/components/timepicker";
import weekBtns from "@/components/weekBtns";
import clientSubscription from "./clientSubscription";

const REWARD_TYPES = [
  { key: "Images and quotes", value: "any" },
  { key: "Just quotes", value: "quote" },
  { key: "Just images", value: "img" },
  { key: "None", value: "none" }
];

const SUBSCRIPTION_STORE = "OwnerSubscriptions";
const CLIENT_SUBSCRIPTION_STORE = "ClientSubscription";

export default {
  components: {
    chooseHabitProgram,
    timePrefences,
    changeEmail,
    userImg,
    changeCard,
    timepicker,
    weekBtns,
    clientSubscription
  },
  data() {
    return {
      timezones,
      subscription: {},
      oldPassword: "",
      password: "",
      confirmation: "",
      reward: "any",
      passwordErrors: "",
      passwordError: "",
      badRequest: {},
      loading: false,
      rewards: REWARD_TYPES,
      userProgram: {},
      changeEmail: true,
      user: {
        work_week: [],
        profile: {}
      },
      imageForm: new FormData(),
      newProfileImage: undefined,
      cLastSubscription: undefined
    };
  },
  created() {
    dataStore
      .findAll(CLIENT_SUBSCRIPTION_STORE, {}, { force: true})
      .then(subs => { 
        this.cLastSubscription = subs[0]
      })
      
    dataStore
      .findAll(SUBSCRIPTION_STORE, { current: true }, { force: true })
      .then(data => {
        if (data[0]) {
          this.subscription = data[0];
        }
      });
    User.getUser(User.id).then(user => {
      Object.assign(this, user);
      this.user = user;
    });
  },
  methods: {
    convertDate(date) {
      return moment(date).format("MMMM DD, YYYY");
    },
    addDay(day) {
      this.user.work_week.push(day);
    },
    imageChange(fieldName, fileList) {
      let file = fileList[0];
      this.imageForm.append("image", file, User.id + file.name);
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = e => {
        this.newProfileImage = e.target.result;
      };
    },
    removeDay(day) {
      this.user.work_week = this.user.work_week.filter(d => d !== day);
    },
    saveSettings() {
      this.loading = true;
      const data = Object.assign({}, this.user);
      delete data.image;
      let updateUser = dataStore.update("User", User.id, data, {
        method: "PATCH"
      });
      let updateImage = undefined;
      if (this.newProfileImage) {
        updateImage = axios.patch(
          `${process.env.VUE_APP_API_URL}user/${this.user.id}/`,
          this.imageForm
        );
      }
      Promise.all([updateUser, updateImage])
        .then(([user]) => {
          User.user = user;
          User.user.image = this.displayImage;
          this.badRequest = {};
          Object.assign(User, this.user);
          snackbar.update("Saved")
        })
        .catch(e => {
          if (e.response.status === 400) {
            snackbar.update("Error")
            this.badRequest = e.response.data;
          } else {
            this.badRequest = {};
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    passwordMatch() {
      return this.password === this.confirmation || "Password doesn't match";
    },
    updateHabitProgram() {
      if (
        new Set(this.userProgram.areas_of_focus).size !== this.userProgram.areas_of_focus.length
      ) {
        this.$emit("displaySnackbar", "Fix error before submitting");
        return;
      }
      this.loading = true;
      axios
        .post(
          `${process.env.VUE_APP_API_URL}user/${
            this.user.id
          }/change_habit_program/`,
          this.userProgram
        )
        .then(() => {
          this.$emit("displaySnackbar", "Saved");
          this.user.theme = this.userProgram.habit_program;
          this.user.groups = this.userProgram.areas_of_focus;
          snackbar.update("Saved");
        })
        .finally(() => (this.loading = false));
    },
    updatePassword() {
      if (this.passwordMatch() !== true) {
        this.passwordErrors = {bad: ["Password doesn't match"]};
        return;
      }

      let data = {
        old_password: this.oldPassword,
        password: this.password,
        confirmation: this.confirmation
      };

      this.passwordError = "";
      this.passwordErrors = "";
      this.loading = true;
      axios
        .post(
          `${process.env.VUE_APP_API_URL}user/${this.user.id}/set_password/`,
          data
        )
        .then(() => {
          this.$emit("displaySnackbar", "Saved");
          this.oldPassword = "";
          this.password = "";
          this.confirmation = "";
        })
        .catch(({ response, status }) => {
          this.passwordErrors = response.data;
          if (response.data.non_field_errors) {
            this.passwordErrors = response.data.non_field_errors[0];
          }

          if (status >= 500) {
            this.passwordErrors = { bad: [
              "There was a server error. Our staff have been notified. Please try again later"
            ]}
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    cancelSubscription() {
      if (!this.subscription.id) {
        return;
      }
      this.subscription.destroy().then(() => {
        this.subscription.renewing = false;
        alert(
          `Your subscription has been canceled. You and your clients will still have access to the software until ${
            this.subscription.scheduled_invoice_at
          }`
        );
      });
    }
  },
  computed: {
    displayImage() {
      return this.newProfileImage || this.user.image;
    },
    appVersion() {
      let v = process.env.VUE_APP_VERSION;
      if (v) {
        return v;
      } else {
        return "";
      }
    }
  }
};
</script>

<style>
.content-wrap {
  display: flex;
  flex-wrap: wrap;
}

.simple-content {
  flex: 1;
  min-width: 200px;
}
</style>
